import { AddressInfoCondensedDisplayStyles } from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { RadioGroupProps } from "@insite/mobius/RadioGroup";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkActionStyles, linkAltStyles } from "@store/Themes/common/mixins/link";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import { css } from "styled-components";

export interface LocationGridProps {
    container?: GridContainerProps;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
}

export interface LocationInfoWindowProps {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    distanceText?: TypographyPresentationProps;
    addressInfo?: AddressInfoCondensedDisplayStyles;
    actionLink?: LinkPresentationProps;
}

export interface LocationProps {
    grid?: LocationGridProps;
    form?: InjectableCss;
    resultsHeader?: InjectableCss;
    resultsText?: TypographyPresentationProps;
    distanceRadioGroup?: RadioGroupProps;
    noResults?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    nameWrapper?: InjectableCss;
    nameText?: TypographyPresentationProps;
    nameLink?: LinkPresentationProps;
    distanceText?: TypographyPresentationProps;
    addressWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    actionLink?: LinkPresentationProps;
    contentModal?: ModalPresentationProps;
    contentModalWrapper?: InjectableCss;
    mapWrapper?: InjectableCss;
    infoWindow?: LocationInfoWindowProps;
}

export const locationProps: LocationProps = {
    form: {
        css: css`
            width: 100%;

            &:not(:last-child) {
                padding-bottom: ${pxToRem(30)};
                border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};
                margin-bottom: ${pxToRem(30)};
            }
        `,
    },
    grid: {
        container: { gap: 30 },
        leftGridItem: {
            width: [12, 12, 6, 6, 6],
            css: css`
                display: block;

                ${breakpoint("sm", "down")} {
                    order: 2;
                }
            `,
        },
        rightGridItem: {
            width: [12, 12, 6, 6, 6],
            css: css`
                display: block;

                ${breakpoint("sm", "down")} {
                    order: 1;
                }
            `,
        },
    },
    resultsHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: ${pxToRem(6, 16)};
            width: 100%;
            padding: ${pxToRem(20)};
            background-color: ${getColor("common.accent")};
        `,
    },
    resultsText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    distanceRadioGroup: {
        horizontal: true,
        css: css`
            ${x => x.theme.radio.groupDefaultProps?.css}
            width: auto;
        `,
    },
    noResults: typographyGetNoResultsAltProps({ spaced: true }),
    wrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(10)};
            padding: ${pxToRem(24, 0)};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(10)};
            flex-grow: 1;
        `,
    },
    nameWrapper: {
        css: css`
            display: flex;
            column-gap: ${pxToRem(10)};
            justify-content: space-between;
        `,
    },
    nameText: {
        css: css`
            ${({ theme }) => css`
                font-weight: ${theme.design.typography.weight.bold};
                line-height: ${theme.design.typography.lineHeight.semi};
            `}
        `,
    },
    nameLink: { css: linkAltStyles() },
    distanceText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
            white-space: nowrap;
        `,
    },
    addressWrapper: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
        `,
    },
    actionLink: {
        css: css`
            ${linkActionStyles()}
            ${inlineSeparatorStyles(pxToRem(20), pxToRem(1), "common.border")}
        `,
    },
    contentModal: { sizeVariant: "small" },
    contentModalWrapper: {},
    mapWrapper: {
        css: css`
            position: sticky;
            top: 0;
            width: 100%;
            height: ${pxToRem(800)};
            min-height: ${pxToRem(400)};
            max-height: 90vh;

            ${breakpoint("sm")} {
                min-height: 0;
            }
        `,
    },
    infoWindow: {
        wrapper: {
            css: css`
                ${({ theme }) => css`
                    font-family: ${theme.design.typography.fontFamily.base};
                    font-size: ${theme.design.typography.size.smaller};
                    line-height: ${theme.design.typography.lineHeight.small};
                `}
            `,
        },
        titleWrapper: {
            css: css`
                ${spacingStyles(null, "0.4em")}
                display: flex;
                column-gap: 1em;
            `,
        },
        title: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.bold};
            `,
        },
        distanceText: {
            css: css`
                white-space: nowrap;
                margin-top: 0.1em;
                font-size: 0.9em;
            `,
        },
        addressInfo: {
            wrapper: {
                css: css`
                    ${spacingStyles(null, "0.3em")}
                    font-style: inherit;
                `,
            },
        },
        actionLink: {
            css: css`
                ${linkActionStyles()}
                ${inlineSeparatorStyles("1em", pxToRem(1), "common.border")}
                font-size: inherit;
            `,
        },
    },
};
